/**
 * Étape 4 — Sélection des travaux par catégorie.
 *
 * UX clé :
 *  - Travaux groupés par catégorie (Rénovation globale / Isolation / Chauffage·ECS / Ventilation / Audit).
 *  - Catégories repliables (accordéon) pour limiter la fatigue de scroll sur mobile :
 *    « Rénovation globale » ouverte par défaut, les autres se déplient à la demande.
 *  - PAS de montants chiffrés ici — seulement des tags d'éligibilité MPR / CEE / Coup-de-pouce / Éco-PTZ / TVA 5,5.
 *  - Sélection multi-cases avec compteur live et badge "Parcours d'ampleur" dès 3 gestes.
 *  - Exclusivité « Rénovation globale » : décocher les gestes si on coche le bouquet global, et vice-versa.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, ChevronDown, Info, Sparkles } from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import { aidsStepCounterLabel, useAidsStore } from './useAidsStore';
import {
  TAG_TONE_CLASSES,
  TRAVAUX_CATEGORIES,
  TRAVAUX_OPTIONS,
  TRAVAUX_TAG_META,
  type TravauxCategoryMeta,
  type TravauxOption,
  type TravauxTag,
} from './travaux-config';

export function Step4Travaux() {
  const profile = useAidsStore((s) => s.profile);
  const travaux = useAidsStore((s) => s.travaux);
  const setTravaux = useAidsStore((s) => s.setTravaux);
  const goNext = useAidsStore((s) => s.goNext);

  const isLocataire = profile === 'locataire';

  // Catégories dépliées : « globale » par défaut + celles contenant déjà une sélection
  // (reprise de parcours). Les autres restent repliées pour limiter le scroll.
  const [openCats, setOpenCats] = useState<Set<string>>(() => {
    const initial = new Set<string>(['globale']);
    for (const cat of TRAVAUX_CATEGORIES) {
      const hasSelection = TRAVAUX_OPTIONS.some(
        (t) => t.category === cat.id && useAidsStore.getState().travaux.includes(t.id),
      );
      if (hasSelection) initial.add(cat.id);
    }
    return initial;
  });

  function toggleCat(id: string) {
    setOpenCats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const distinctGestes = new Set(
    travaux
      .map((id) => TRAVAUX_OPTIONS.find((t) => t.id === id))
      .filter((t): t is TravauxOption => !!t)
      .filter((t) => t.category !== 'audit' && t.category !== 'globale'),
  );
  const hasRenoGlobale = travaux.includes('reno-globale');
  const isAmpleur = hasRenoGlobale || distinctGestes.size >= 3;

  function toggle(id: string) {
    const isGlobale = id === 'reno-globale';
    let next: string[];

    if (travaux.includes(id)) {
      next = travaux.filter((x) => x !== id);
    } else if (isGlobale) {
      // Si on coche Rénovation globale → on garde uniquement Rénovation globale + Audit
      next = travaux.filter((x) => x === 'audit');
      next.push(id);
    } else {
      // Si on coche un geste alors que Rénovation globale est cochée → décocher Rénovation globale
      next = hasRenoGlobale ? travaux.filter((x) => x !== 'reno-globale') : [...travaux];
      next.push(id);
    }
    setTravaux(next);
  }

  const canContinue = travaux.length > 0;

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
        {aidsStepCounterLabel(3, profile)}
      </p>
      <h2 className="mt-2 h-section">
        Quels travaux envisagez-vous&nbsp;?
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-500">
        {isLocataire ? (
          <>
            En tant que locataire, certaines primes CEE restent accessibles
            (accord écrit du propriétaire requis).
          </>
        ) : (
          <>
            Sélectionnez un parcours global <em>ou</em> plusieurs gestes —
            <strong className="text-ink"> 3 gestes ou + ouvrent le Parcours accompagné </strong>
            (ex-rénovation d'ampleur, plafond 63 000 €).
          </>
        )}
      </p>

      {/* ── Compteur + état "ampleur" ─────────────────────── */}
      <div className="mt-5 flex flex-wrap items-center gap-2 rounded-xl border border-stone-200 bg-white px-3.5 py-2.5">
        <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-md bg-stone-900 px-1.5 text-[12px] font-bold tabular-nums text-white">
          {travaux.length}
        </span>
        <span className="text-[12.5px] text-stone-600">
          {travaux.length === 0
            ? 'travail sélectionné'
            : travaux.length === 1
              ? 'travail sélectionné'
              : 'travaux sélectionnés'}
        </span>
        <AnimatePresence>
          {!isLocataire && isAmpleur && (
            <motion.span
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: easeStripe }}
              className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-brand-500 to-accent-500 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wider text-white shadow-[0_2px_8px_-2px_rgba(16,185,129,0.45)]"
            >
              <Sparkles className="h-3 w-3" strokeWidth={2.4} />
              Parcours accompagné
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* ── Catégories (repliables) ───────────────────────── */}
      <div className="mt-6 space-y-4">
        {TRAVAUX_CATEGORIES.map((cat) => (
          <CategoryBlock
            key={cat.id}
            category={cat}
            selected={travaux}
            onToggle={toggle}
            open={openCats.has(cat.id)}
            onToggleOpen={() => toggleCat(cat.id)}
            disabled={cat.id !== 'audit' && cat.id !== 'globale' && hasRenoGlobale}
            disabledReason={
              cat.id !== 'audit' && cat.id !== 'globale' && hasRenoGlobale
                ? "Inclus dans le parcours d'ampleur"
                : null
            }
          />
        ))}
      </div>

      {/* ── CTA ───────────────────────────────────────────── */}
      <Button
        variant="brand"
        size="xl"
        disabled={!canContinue}
        onClick={goNext}
        className="mt-7 w-full gap-2"
      >
        Continuer vers mes coordonnées
        <ArrowRight className="h-4 w-4" />
      </Button>

      <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-stone-400">
        <Info className="h-3 w-3" strokeWidth={1.8} />
        Les montants détaillés s'affichent à l'étape résultat
      </p>
    </div>
  );
}

/* ─── Bloc de catégorie ─────────────────────────────────── */

function CategoryBlock({
  category,
  selected,
  onToggle,
  open,
  onToggleOpen,
  disabled,
  disabledReason,
}: {
  category: TravauxCategoryMeta;
  selected: string[];
  onToggle: (id: string) => void;
  open: boolean;
  onToggleOpen: () => void;
  disabled: boolean;
  disabledReason: string | null;
}) {
  const items = TRAVAUX_OPTIONS.filter((t) => t.category === category.id);
  const selectedCount = items.filter((t) => selected.includes(t.id)).length;
  const Icon = category.icon;

  return (
    <section
      aria-labelledby={`cat-${category.id}`}
      className={cn(
        'overflow-hidden rounded-2xl border transition-colors',
        open ? 'border-stone-200 bg-white' : 'border-stone-200/80 bg-stone-50/50',
      )}
    >
      {/* En-tête cliquable (accordéon) */}
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`cat-panel-${category.id}`}
        onClick={onToggleOpen}
        className="flex w-full cursor-pointer items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-stone-50/80"
      >
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
            category.variant === 'global'
              ? 'bg-gradient-to-br from-brand-100 to-accent-100 text-brand-700'
              : 'bg-stone-100 text-stone-700',
          )}
        >
          <Icon className="h-4 w-4" strokeWidth={1.8} />
        </span>
        <span className="min-w-0 flex-1">
          <span
            id={`cat-${category.id}`}
            className="flex flex-wrap items-center gap-2 font-display text-[15px] font-bold tracking-tight text-ink"
          >
            {category.label}
            {category.variant === 'global' && (
              <span className="rounded-md bg-gradient-to-r from-brand-600 to-accent-500 bg-clip-text text-[10px] font-bold uppercase tracking-wider text-transparent">
                Recommandé
              </span>
            )}
            {selectedCount > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-600 px-1.5 text-[10.5px] font-bold tabular-nums text-white">
                {selectedCount}
              </span>
            )}
          </span>
          <span className="mt-0.5 block text-[12px] leading-relaxed text-stone-500">
            {category.description}
          </span>
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            'mt-2 h-4 w-4 shrink-0 text-stone-400 transition-transform duration-200',
            open && 'rotate-180',
          )}
          strokeWidth={2}
        />
      </button>

      {/* Contenu */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`cat-panel-${category.id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: easeStripe }}
            className="overflow-hidden"
          >
            <div className="grid gap-2 px-4 pb-4 sm:grid-cols-2">
              {items.map((item) => (
                <TravailCard
                  key={item.id}
                  item={item}
                  selected={selected.includes(item.id)}
                  onToggle={onToggle}
                  disabled={disabled}
                  disabledReason={disabledReason}
                  featured={category.variant === 'global'}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

/* ─── Carte travail ─────────────────────────────────────── */

function TravailCard({
  item,
  selected,
  onToggle,
  disabled,
  disabledReason,
  featured,
}: {
  item: TravauxOption;
  selected: boolean;
  onToggle: (id: string) => void;
  disabled: boolean;
  disabledReason: string | null;
  featured: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-disabled={disabled}
      onClick={() => !disabled && onToggle(item.id)}
      title={disabled && disabledReason ? disabledReason : undefined}
      className={cn(
        'group relative flex items-start gap-3 overflow-hidden rounded-xl border-2 p-3.5 text-left transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-1',
        featured && 'sm:col-span-2',
        disabled && 'cursor-not-allowed opacity-50',
        selected
          ? 'border-brand-500 bg-brand-50/80 shadow-[0_4px_20px_-8px_rgba(16,185,129,0.25)]'
          : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50/40',
      )}
    >
      {/* Glow gradient when selected */}
      {selected && (
        <motion.span
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-50/0 via-brand-50/50 to-accent-50/30"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25, ease: easeStripe }}
        />
      )}

      <span
        className={cn(
          'relative mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-all duration-150',
          selected
            ? 'border-brand-500 bg-brand-500'
            : 'border-stone-300 bg-white group-hover:border-stone-400',
        )}
      >
        <AnimatePresence>
          {selected && (
            <motion.span
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.4 }}
              transition={{ duration: 0.15, ease: easeStripe }}
            >
              <Check className="h-3 w-3 text-white" strokeWidth={3} />
            </motion.span>
          )}
        </AnimatePresence>
      </span>

      <div className="relative min-w-0 flex-1">
        <p
          className={cn(
            'text-[13.5px] font-semibold leading-snug',
            selected ? 'text-brand-900' : 'text-ink',
          )}
        >
          {item.label}
        </p>
        <p className="mt-0.5 text-[11.5px] leading-relaxed text-stone-500">
          {item.sublabel}
        </p>

        {/* Tags d'éligibilité */}
        <div className="mt-2 flex flex-wrap gap-1">
          {item.tags.map((tag) => (
            <Tag key={tag} tag={tag} />
          ))}
        </div>
      </div>
    </button>
  );
}

/* ─── Tag d'éligibilité ─────────────────────────────────── */

function Tag({ tag }: { tag: TravauxTag }) {
  const meta = TRAVAUX_TAG_META[tag];
  return (
    <span
      title={meta.full}
      className={cn(
        'inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset',
        TAG_TONE_CLASSES[meta.tone],
      )}
    >
      {meta.label}
    </span>
  );
}
